/**
 * NC500 Wishlist — an interactive checklist of the North Coast 500 landmarks
 * I plan to fly. Each row lists the target and my planned drone tracking shot.
 * Visitors can tick landmarks off; state persists in the browser via
 * localStorage so their progress survives page reloads.
 *
 * Adding a new landmark: append an object to the LANDMARKS array below.
 * Data model: { id, name, sublocation, plannedShot }
 */

import { useEffect, useMemo, useState } from "react";
import { Check, MapPin, RotateCcw } from "lucide-react";
import { Reveal, MaskedLine, HighlandBackdrop } from "@/components/Reveal";
import usePageMeta from "@/lib/usePageMeta";

/* -------------------------------------------------------------- */
/*  ⇩ EDIT HERE — add / remove landmarks as the route evolves.     */
/* -------------------------------------------------------------- */
const LANDMARKS = [
    { id: "inverness",       name: "Inverness",             sublocation: "Highland capital",         plannedShot: "Slow rise over the River Ness at first light, revealing the castle and cathedral spires." },
    { id: "black-isle",      name: "Black Isle",            sublocation: "Fortrose & Cromarty",       plannedShot: "Low tracking sweep along the Chanonry coastline into the Moray Firth." },
    { id: "kessock-bridge",  name: "Kessock Bridge",        sublocation: "Inverness Firth",           plannedShot: "Reverse pull-back from the bridge deck, exposing the full span at golden hour." },
    { id: "corrieshalloch",  name: "Corrieshalloch Gorge",  sublocation: "Braemore Junction",         plannedShot: "Vertical descent into the gorge, following the Falls of Measach downward." },
    { id: "ullapool",        name: "Ullapool",              sublocation: "Loch Broom",                 plannedShot: "Wide orbital over the harbour at dusk with the Summer Isles on the horizon." },
    { id: "ardvreck",        name: "Ardvreck Castle",       sublocation: "Loch Assynt",               plannedShot: "Cinematic push-in across the loch, castle reflected in still water." },
    { id: "suilven",         name: "Suilven",               sublocation: "Assynt",                    plannedShot: "High-altitude reveal from behind the ridge, the peak emerging through drifting cloud." },
    { id: "stoer",           name: "Old Man of Stoer",      sublocation: "Stoer Peninsula",           plannedShot: "Slow side-track along the sea cliff, the stack framed against Atlantic breakers." },
    { id: "kylesku",         name: "Kylesku Bridge",        sublocation: "Loch a' Chàirn Bhàin",      plannedShot: "Overhead crawl following a car crossing the bridge, revealing both lochs at once." },
    { id: "durness",         name: "Durness Beaches",       sublocation: "Sango Sands / Balnakeil",  plannedShot: "Low glide across turquoise water toward the white-sand shore." },
    { id: "smoo-cave",       name: "Smoo Cave",             sublocation: "Durness",                   plannedShot: "Slow descent through the sea-arch mouth into the interior cavern." },
    { id: "cape-wrath",      name: "Cape Wrath",            sublocation: "North-west tip",            plannedShot: "Big cinematic reveal over the lighthouse with the Atlantic sprawling to the horizon." },
    { id: "faraid-head",     name: "Faraid Head",           sublocation: "Balnakeil peninsula",       plannedShot: "Tracking shot along the dune ridge, ending on the WWII lookout station." },
    { id: "loch-eriboll",    name: "Loch Eriboll",          sublocation: "Sutherland north coast",   plannedShot: "Long linear tracking flight down the eastern shore into the deep loch head." },
    { id: "strathy-point",   name: "Strathy Point",          sublocation: "North coast",              plannedShot: "Orbit over the lighthouse peninsula as waves detonate against the base." },
    { id: "dunnet-head",     name: "Dunnet Head",           sublocation: "Britain's most northerly point", plannedShot: "Rise-and-tilt from the cliff edge revealing the Orkney islands across the Pentland Firth." },
    { id: "john-o-groats",   name: "John O'Groats",         sublocation: "Caithness",                 plannedShot: "Golden-hour orbit over the harbour and the signpost, ending on the Stacks." },
    { id: "duncansby",       name: "Duncansby Stacks",      sublocation: "Duncansby Head",            plannedShot: "Low-altitude side-track past each stack, framing them against open sea." },
    { id: "whaligoe",        name: "Whaligoe Steps",        sublocation: "Ulbster",                   plannedShot: "Vertical descent down the 365 steps into the cliff-locked harbour." },
    { id: "dunrobin",        name: "Dunrobin Castle",       sublocation: "Golspie",                   plannedShot: "Slow orbital over the French-style gardens, castle façade catching the sun." },
    { id: "loch-fleet",      name: "Loch Fleet",            sublocation: "Sutherland east coast",     plannedShot: "Low sweep across the tidal basin at low tide, seal colony visible on the sandbanks." },
    { id: "bealach-na-ba",   name: "Bealach na Bà",         sublocation: "Applecross Peninsula",      plannedShot: "Tracking flight up the hairpin bends, revealing the pass from below." },
    { id: "applecross",      name: "Applecross Bay",        sublocation: "Wester Ross",               plannedShot: "Wide orbital over the bay at sunset with Skye and Raasay on the horizon." },
    { id: "torridon",        name: "Torridon",              sublocation: "Loch Torridon & Liathach",  plannedShot: "Cinematic sweep along the loch, tilting up to Liathach's quartzite crown." },
];

const STORAGE_KEY = "dtscotslad:nc500-wishlist:v1";

export default function NC500Wishlist() {
    usePageMeta({
        title: "NC500 Wishlist — DTScotslad",
        description: "An interactive checklist of North Coast 500 landmarks I plan to fly, with the drone tracking shot planned for each.",
    });

    const [visited, setVisited] = useState({});

    // Load once on mount, persist on every change.
    useEffect(() => {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            if (raw) setVisited(JSON.parse(raw));
        } catch { /* corrupt payload — reset silently */ }
    }, []);
    useEffect(() => {
        try { localStorage.setItem(STORAGE_KEY, JSON.stringify(visited)); } catch { /* quota full — ignore */ }
    }, [visited]);

    const toggle = (id) => setVisited((v) => ({ ...v, [id]: !v[id] }));
    const resetAll = () => setVisited({});

    const doneCount = useMemo(() => LANDMARKS.filter((l) => visited[l.id]).length, [visited]);
    const total = LANDMARKS.length;
    const pct = Math.round((doneCount / total) * 100);

    return (
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 py-24 lg:py-32" data-testid="nc500-wishlist-page">
            {/* ---- Header ---- */}
            <header className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16 relative">
                <div className="lg:col-span-8">
                    <Reveal>
                        <div className="overline mb-4" style={{ color: "#0055A4", fontWeight: 700 }}>
                            <span className="editorial-rule" style={{ background: "#0055A4" }} />
                            NC500 · Wishlist
                        </div>
                    </Reveal>
                    <h1
                        className="font-serif font-light tracking-tighter leading-[0.9]"
                        style={{ fontSize: "clamp(2.6rem, 7vw, 6.5rem)" }}
                        data-testid="nc500-title"
                    >
                        <div><MaskedLine delay={0.1}>North Coast 500,</MaskedLine></div>
                        <div>
                            <MaskedLine delay={0.3}>
                                <span className="italic" style={{ color: "#0055A4" }}>landmark by landmark.</span>
                            </MaskedLine>
                        </div>
                    </h1>
                </div>
                <Reveal delay={0.4} className="lg:col-span-4 self-end">
                    <p className="text-[var(--sb-text-dim)] text-lg leading-relaxed">
                        The full route target list with the drone tracking shot planned for each. Tick landmarks off as I fly them &mdash; your progress is saved in this browser.
                    </p>
                </Reveal>
            </header>

            {/* ---- Progress bar ---- */}
            <div className="mb-10 border border-[var(--sb-border)] p-5 lg:p-6" data-testid="nc500-progress">
                <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
                    <div>
                        <div className="overline mb-1" style={{ color: "#0055A4", fontWeight: 700 }}>Progress</div>
                        <div className="font-serif text-2xl tracking-tight">
                            <span className="tabular-nums" data-testid="nc500-progress-count">{doneCount}</span>
                            <span className="text-[var(--sb-text-muted)]"> / {total} </span>
                            <span className="text-[var(--sb-text-dim)] text-base">landmarks flown</span>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={resetAll}
                        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[var(--sb-text-muted)] hover:text-[var(--sb-text)] transition-colors border-b border-[var(--sb-border)] hover:border-[var(--sb-brand)] pb-1"
                        data-testid="nc500-reset"
                    >
                        <RotateCcw size={12} /> Reset all
                    </button>
                </div>
                <div className="h-[3px] w-full bg-[var(--sb-border)] overflow-hidden rounded-full">
                    <div
                        className="h-full transition-[width] duration-500"
                        style={{ width: `${pct}%`, background: "#0055A4" }}
                        data-testid="nc500-progress-bar"
                    />
                </div>
            </div>

            {/* ---- Table ---- */}
            <div className="border border-[var(--sb-border)] overflow-hidden" data-testid="nc500-table">
                {/* Header row — desktop only */}
                <div className="hidden md:grid md:grid-cols-12 gap-4 px-6 py-4 border-b border-[var(--sb-border)] bg-[var(--sb-surface)]" data-testid="nc500-thead">
                    <div className="col-span-4 text-xs uppercase tracking-[0.24em] text-[var(--sb-text-muted)]">
                        Target Landmark
                    </div>
                    <div className="col-span-6 text-xs uppercase tracking-[0.24em] text-[var(--sb-text-muted)]">
                        Planned Drone Tracking Shot
                    </div>
                    <div className="col-span-2 text-xs uppercase tracking-[0.24em] text-[var(--sb-text-muted)] text-right">
                        Status
                    </div>
                </div>

                {/* Rows */}
                <ul>
                    {LANDMARKS.map((l, idx) => {
                        const isDone = !!visited[l.id];
                        return (
                            <li
                                key={l.id}
                                className={`grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-4 px-5 md:px-6 py-5 md:py-4 border-b border-[var(--sb-border)] last:border-b-0 transition-colors ${
                                    isDone ? "bg-[rgba(0,85,164,0.06)]" : ""
                                }`}
                                data-testid={`nc500-row-${l.id}`}
                            >
                                {/* Landmark name */}
                                <div className="md:col-span-4 flex items-start gap-3">
                                    <span className="font-mono text-xs text-[var(--sb-text-muted)] mt-1 tabular-nums shrink-0">{String(idx + 1).padStart(2, "0")}</span>
                                    <div>
                                        <div className={`font-serif text-lg leading-tight tracking-tight ${isDone ? "line-through opacity-70" : ""}`}>
                                            {l.name}
                                        </div>
                                        <div className="text-[11px] uppercase tracking-[0.22em] text-[var(--sb-text-muted)] mt-1 flex items-center gap-1.5">
                                            <MapPin size={10} /> {l.sublocation}
                                        </div>
                                    </div>
                                </div>

                                {/* Planned shot */}
                                <div className="md:col-span-6 text-sm text-[var(--sb-text-dim)] leading-relaxed">
                                    <div className="md:hidden overline mb-1" style={{ color: "#0055A4" }}>Planned shot</div>
                                    {l.plannedShot}
                                </div>

                                {/* Status checkbox */}
                                <div className="md:col-span-2 md:text-right">
                                    <button
                                        type="button"
                                        onClick={() => toggle(l.id)}
                                        aria-pressed={isDone}
                                        data-testid={`nc500-check-${l.id}`}
                                        className="inline-flex items-center gap-3 text-xs uppercase tracking-[0.22em] transition-colors group"
                                        style={{ color: isDone ? "#0055A4" : "var(--sb-text-muted)" }}
                                    >
                                        <span
                                            className="w-5 h-5 border flex items-center justify-center transition-colors shrink-0"
                                            style={{
                                                borderColor: isDone ? "#0055A4" : "var(--sb-border)",
                                                background: isDone ? "#0055A4" : "transparent",
                                            }}
                                            aria-hidden
                                        >
                                            {isDone && <Check size={12} className="text-white" strokeWidth={3} />}
                                        </span>
                                        <span className="whitespace-nowrap">
                                            {isDone ? "Visited" : "Not visited yet"}
                                        </span>
                                    </button>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            </div>

            <p className="text-xs text-[var(--sb-text-muted)] mt-6 max-w-2xl">
                Progress is stored locally in your browser only — clearing cookies or switching devices will reset your list.
            </p>

            <HighlandBackdrop variant="soft" />
        </div>
    );
}
